import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"
import type { UseFormSetValue } from "react-hook-form"

import {
  type QuestionCreate,
  type QuestionUpdate,
  QuestionsService,
} from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

interface QuestionFormData {
  question_text: string
  rationale: string
  options: Array<{ value: string }>
  correct_answer: number
  subject_ids: string[]
  topic_ids: string[]
  allow_practice_quiz: boolean
}

interface QuestionFormContextValue {
  // State
  mode: "add" | "edit"
  questionId?: string
  selectedSubjects: Array<{ id: string; name: string }>
  selectedTopics: Array<{ id: string; name: string }>
  loadingQuestion: boolean
  isSubmitting: boolean
  showRescoreConfirmation: boolean
  rescoreImpact: {
    affected_answers_count: number
    affected_attempts_count: number
    affected_quizzes_count: number
  } | null

  // Actions
  setSelectedSubjects: (subjects: Array<{ id: string; name: string }>) => void
  setSelectedTopics: (topics: Array<{ id: string; name: string }>) => void
  handleSubjectsChange: (subjects: Array<{ id: string; name: string }>) => void
  handleTopicsChange: (topics: Array<{ id: string; name: string }>) => void
  onSubmit: (data: QuestionFormData) => void
  confirmRescoreAndSubmit: () => void
  cancelRescoreConfirmation: () => void
  initializeForm: (setValue: UseFormSetValue<QuestionFormData>) => void
}

const QuestionFormContext = createContext<QuestionFormContextValue | undefined>(
  undefined,
)

interface QuestionFormProviderProps {
  mode: "add" | "edit"
  questionId?: string
  children: ReactNode
}

export function QuestionFormProvider({
  mode,
  questionId,
  children,
}: QuestionFormProviderProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [setValueFn, setSetValueFn] =
    useState<UseFormSetValue<QuestionFormData> | null>(null)
  const [showRescoreConfirmation, setShowRescoreConfirmation] = useState(false)
  const [pendingQuestionData, setPendingQuestionData] =
    useState<QuestionUpdate | null>(null)
  const [rescoreImpact, setRescoreImpact] = useState<{
    affected_answers_count: number
    affected_attempts_count: number
    affected_quizzes_count: number
  } | null>(null)

  // Load existing question for edit mode
  const { data: existingQuestion, isLoading: loadingQuestion } = useQuery({
    queryKey: ["question", questionId],
    queryFn: () =>
      QuestionsService.getQuestion({ questionId: questionId as string }),
    enabled: mode === "edit" && !!questionId,
  })

  // Populate form with existing question data
  useEffect(() => {
    if (existingQuestion && mode === "edit" && setValueFn) {
      setValueFn("question_text", existingQuestion.question_text)
      setValueFn("rationale", existingQuestion.rationale || "")
      setValueFn(
        "options",
        existingQuestion.options.map((opt) => ({ value: opt })),
      )
      setValueFn("correct_answer", existingQuestion.correct_answer)
      setValueFn(
        "subject_ids",
        existingQuestion.subjects.map((s) => s.id),
      )
      setValueFn(
        "topic_ids",
        existingQuestion.topics.map((t) => t.id),
      )
      setValueFn("allow_practice_quiz", existingQuestion.allow_practice_quiz)
      setSelectedSubjects(
        existingQuestion.subjects.map((s) => ({ id: s.id, name: s.name })),
      )
      setSelectedTopics(
        existingQuestion.topics.map((t) => ({ id: t.id, name: t.name })),
      )
    }
  }, [existingQuestion, mode, setValueFn])

  const createMutation = useMutation({
    mutationFn: (data: QuestionCreate) =>
      QuestionsService.createQuestion({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast("Question created successfully")
      queryClient.invalidateQueries({ queryKey: ["questions"] })
      navigate({ to: "/questions" })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: QuestionUpdate) =>
      QuestionsService.updateQuestion({
        questionId: questionId as string,
        requestBody: data,
      }),
    onSuccess: () => {
      showSuccessToast("Question updated successfully")
      queryClient.invalidateQueries({ queryKey: ["questions"] })
      queryClient.invalidateQueries({ queryKey: ["question", questionId] })
      navigate({ to: "/questions" })
    },
  })

  const buildQuestionData = (data: QuestionFormData): QuestionUpdate => {
    return {
      question_text: data.question_text,
      rationale: data.rationale || null,
      options: data.options.map((opt) => opt.value),
      correct_answer: data.correct_answer,
      subject_ids: data.subject_ids,
      // Pass an empty array when no topics are selected so updates clear associations
      topic_ids: data.topic_ids,
      allow_practice_quiz: data.allow_practice_quiz,
    }
  }

  const onSubmit = async (data: QuestionFormData) => {
    const questionData = buildQuestionData(data)
    if (mode === "add") {
      createMutation.mutate(questionData as QuestionCreate)
    } else {
      try {
        await updateMutation.mutateAsync(questionData)
      } catch (err) {
        const apiErr = err as ApiError
        const errBody = apiErr.body as any
        const errDetail = errBody?.detail ?? errBody
        if (
          apiErr.status === 409 &&
          errDetail?.requires_rescore_confirmation === true
        ) {
          setPendingQuestionData(questionData)
          setRescoreImpact({
            affected_answers_count: errDetail.affected_answers_count ?? 0,
            affected_attempts_count: errDetail.affected_attempts_count ?? 0,
            affected_quizzes_count: errDetail.affected_quizzes_count ?? 0,
          })
          setShowRescoreConfirmation(true)
          return
        }
        handleError(apiErr)
      }
    }
  }

  const confirmRescoreAndSubmit = async () => {
    if (!pendingQuestionData) return
    try {
      await updateMutation.mutateAsync({
        ...pendingQuestionData,
        confirm_rescore: true,
      })
      setShowRescoreConfirmation(false)
      setPendingQuestionData(null)
      setRescoreImpact(null)
    } catch (err) {
      handleError(err as ApiError)
    }
  }

  const cancelRescoreConfirmation = () => {
    setShowRescoreConfirmation(false)
    setPendingQuestionData(null)
    setRescoreImpact(null)
  }

  const handleSubjectsChange = (
    subjects: Array<{ id: string; name: string }>,
  ) => {
    setSelectedSubjects(subjects)
    if (setValueFn) {
      setValueFn(
        "subject_ids",
        subjects.map((s) => s.id),
        { shouldValidate: true },
      )
    }
  }

  const handleTopicsChange = (topics: Array<{ id: string; name: string }>) => {
    setSelectedTopics(topics)
    if (setValueFn) {
      setValueFn(
        "topic_ids",
        topics.map((t) => t.id),
      )
    }
  }

  const initializeForm = (setValue: UseFormSetValue<QuestionFormData>) => {
    setSetValueFn(() => setValue)
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const value: QuestionFormContextValue = {
    mode,
    questionId,
    selectedSubjects,
    selectedTopics,
    loadingQuestion,
    isSubmitting,
    showRescoreConfirmation,
    rescoreImpact,
    setSelectedSubjects,
    setSelectedTopics,
    handleSubjectsChange,
    handleTopicsChange,
    onSubmit,
    confirmRescoreAndSubmit,
    cancelRescoreConfirmation,
    initializeForm,
  }

  return (
    <QuestionFormContext.Provider value={value}>
      {children}
    </QuestionFormContext.Provider>
  )
}

export function useQuestionForm() {
  const context = useContext(QuestionFormContext)
  if (!context) {
    throw new Error("useQuestionForm must be used within QuestionFormProvider")
  }
  return context
}

export type { QuestionFormData }
